import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { environment } from '@env/environment';
import { AuthService } from '../auth/auth.service';
import { resolveTenantRedirect } from '../tenant/tenant-redirect';
import { WINDOW_LOCATION } from '../tenant/window-location.token';

/**
 * Reubica al usuario en el subdominio de su tenant al entrar en `/app`.
 *
 * Vive en un guard y no sólo en el `provideAppInitializer` porque el
 * initializer corre UNA vez, al arrancar la aplicación. Tras iniciar sesión,
 * `login.component` hace `router.navigate(['/app'])` — navegación del lado del
 * cliente— y el initializer ya no vuelve a ejecutarse: el usuario se quedaba en
 * el apex hasta la siguiente recarga. Un guard sobre `/app` cubre los tres
 * caminos de entrada (login, pestaña nueva y marcador) con un solo punto.
 *
 * Depende de que `me` esté cargado: lo garantiza `AuthService.afterAuth()`, que
 * encadena `fetchMe()` antes de que el componente de login navegue.
 *
 * Se usa `state.url` y NO `location.href` para construir el destino: durante la
 * navegación, `location.href` todavía apunta a la página ANTERIOR (`/login`), y
 * usarlo mandaría al usuario a `{tenant}.dominio/login` en vez de a `/app`.
 */
export const tenantSubdomainGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const location = inject(WINDOW_LOCATION);

  const redirect = resolveTenantRedirect({
    href: `${location.origin}${state.url}`,
    suffix: environment.domainSuffix,
    tenantSubdomain: auth.me()?.tenant?.subdomain ?? null,
    enabled: environment.tenantSubdomainRedirect && !environment.useMocks,
  });

  if (!redirect) return true;

  // replace y no assign: si el origen anterior quedara en el historial, el botón
  // «atrás» devolvería al usuario justo al sitio del que lo acabamos de sacar.
  location.replace(redirect);
  // Se abandona esta navegación: el navegador ya va camino de otro origen.
  return false;
};
